/* RestaurantModeCard — iter424. Vision OCR на ресторантско меню + phenotype ranking. */
import React, { useRef, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Camera, Upload, Loader2, Sparkles, ChefHat, CheckCircle2, AlertCircle, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

export default function RestaurantModeCard() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const fileRef = useRef(null);

  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(bg ? "Файлът е твърде голям (макс 5MB)" : "File too large (max 5MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPreview({ file, dataUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const handleScan = async () => {
    if (!preview?.file) return;
    setScanning(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", preview.file);
      const r = await axios.post(`${API}/restaurant-scan`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setResult(r.data);
      toast.success(bg
        ? `Сканирани ${r.data?.dishes_count} ястия`
        : `Scanned ${r.data?.dishes_count} dishes`);
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка при сканиране" : "Scan failed"));
    } finally { setScanning(false); }
  };

  const reset = () => {
    setPreview(null); setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <Card className={`border ${isDark ? "border-orange-500/30 bg-gradient-to-br from-orange-500/5 to-amber-500/5" : "border-orange-200 bg-gradient-to-br from-orange-50/40 to-amber-50/30"}`}
          data-testid="restaurant-mode-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-orange-500 to-amber-500 text-white">
            <Camera className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Restaurant Mode — AI избор от менюто" : "Restaurant Mode — AI menu picker"}
            </h3>
            <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg
                ? "Снимка на ресторантско меню → AI избира top 3 ястия за Вашия фенотип."
                : "Photo of restaurant menu → AI picks top 3 dishes for your phenotype."}
            </p>
          </div>
        </div>

        {/* Upload area */}
        {!preview && !result && (
          <div className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
            isDark ? "border-orange-500/40 hover:bg-orange-500/5" : "border-orange-300 hover:bg-orange-50/60"
          }`} onClick={() => fileRef.current?.click()} data-testid="restaurant-upload-zone">
            <Upload className={`w-8 h-8 mx-auto mb-2 ${isDark ? "text-orange-300" : "text-orange-500"}`} />
            <p className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Изберете снимка на менюто" : "Choose menu photo"}
            </p>
            <p className={`text-[10px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {bg ? "JPG, PNG, WebP · до 5MB" : "JPG, PNG, WebP · max 5MB"}
            </p>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                   onChange={handleFileSelect} className="hidden" data-testid="restaurant-file-input" />
          </div>
        )}

        {/* Preview before scan */}
        {preview && !result && (
          <div className="space-y-2">
            <div className="relative rounded-xl overflow-hidden border" style={{ maxHeight: 240 }}>
              <img src={preview.dataUrl} alt="menu preview"
                   className="w-full object-contain" style={{ maxHeight: 240 }} />
              <button onClick={reset}
                      className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center"
                      data-testid="restaurant-reset-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
            <Button onClick={handleScan} disabled={scanning}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                    data-testid="restaurant-scan-btn">
              {scanning ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />{bg ? "AI чете менюто…" : "AI reading menu…"}</>
              ) : (
                <><Sparkles className="w-3.5 h-3.5 mr-1" />{bg ? "Сканирай менюто" : "Scan menu"}</>
              )}
            </Button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-2.5" data-testid="restaurant-result">
            <div className="flex items-center justify-between">
              <div>
                {result.restaurant_name && (
                  <p className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                    🍴 {result.restaurant_name}
                  </p>
                )}
                <p className={`text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  {bg ? `Сканирани ${result.dishes_count} ястия` : `Scanned ${result.dishes_count} dishes`}
                  {result.cycle_phase_bg && result.cycle_phase_bg !== "няма данни" && ` · ${result.cycle_phase_bg}`}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={reset} data-testid="restaurant-new-scan-btn">
                <Camera className="w-3.5 h-3.5 mr-1" />{bg ? "Нова снимка" : "New photo"}
              </Button>
            </div>

            {/* General advice */}
            {result.general_advice_bg && (
              <div className={`p-2.5 rounded-xl ${isDark ? "bg-orange-500/15 border border-orange-500/30" : "bg-orange-50 border border-orange-200"}`}>
                <p className={`text-[11px] ${isDark ? "text-orange-100" : "text-orange-900"}`}>
                  💡 {result.general_advice_bg}
                </p>
              </div>
            )}

            {/* Top picks */}
            {Array.isArray(result.top_picks) && result.top_picks.length > 0 && (
              <div className="space-y-2" data-testid="restaurant-top-picks">
                <p className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                  <CheckCircle2 className="w-3 h-3" /> {bg ? "Препоръчвам Ви" : "Recommended"}
                </p>
                {result.top_picks.map((p, i) => {
                  // Find matching dish from full list for metrics
                  const dish = (result.dishes || []).find((d) =>
                    (d.name_bg || d.name || "").toLowerCase() === (p.dish_name || "").toLowerCase()
                  ) || {};
                  return (
                    <div key={i} className={`p-2.5 rounded-xl border ${
                      isDark ? "border-emerald-500/30 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50/40"
                    }`} data-testid={`restaurant-pick-${i}`}>
                      <div className="flex items-start gap-2">
                        <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                          isDark ? "bg-emerald-500 text-white" : "bg-emerald-500 text-white"
                        }`}>{p.rank || i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                            {p.dish_name}
                          </p>
                          {(dish.kcal || dish.dil_g) && (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {dish.kcal && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-rose-500/20 text-rose-200" : "bg-rose-50 text-rose-700"}`}>{Math.round(dish.kcal)} kcal</span>}
                              {dish.dil_g != null && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-orange-500/20 text-orange-200" : "bg-orange-50 text-orange-700"}`}>DIL {dish.dil_g}g</span>}
                              {dish.nis_pct != null && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-violet-500/20 text-violet-200" : "bg-violet-50 text-violet-700"}`}>NIS {dish.nis_pct}%</span>}
                              {dish.uis_pct != null && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-amber-500/20 text-amber-200" : "bg-amber-50 text-amber-700"}`}>UIS {dish.uis_pct}%</span>}
                            </div>
                          )}
                          <p className={`text-[11px] mt-1 leading-relaxed ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                            {p.reason_bg}
                          </p>
                          {p.modification_bg && (
                            <p className={`text-[10px] italic mt-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                              💬 {p.modification_bg}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* To avoid */}
            {Array.isArray(result.to_avoid) && result.to_avoid.length > 0 && (
              <div className="space-y-1.5" data-testid="restaurant-avoid">
                <p className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                  <AlertCircle className="w-3 h-3" /> {bg ? "Заслужава да се избегне" : "Best to skip"}
                </p>
                {result.to_avoid.map((a, i) => (
                  <div key={i} className={`p-2 rounded-lg ${isDark ? "bg-amber-500/10" : "bg-amber-50/40"}`}>
                    <p className={`text-[11px] font-bold ${isDark ? "text-amber-100" : "text-amber-900"}`}>{a.dish_name}</p>
                    <p className={`text-[10px] italic ${isDark ? "text-amber-200/80" : "text-amber-700/80"}`}>{a.reason_bg}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
